import { InfoGroup } from "#/components/InfoGroup/InfoGroup";
import { InternalAccount } from "#/components/InternalAccount/InternalAccount";
import { useAccounts } from "#/hooks/useAccounts";
import { useAccount } from "#/store/wallet/account/account";
import { useAccountState } from "#/store/wallet/account/accountState";
import { type PortfolioSymbolType } from "#/types/markets";
import { getImpactColor } from "#/utils/getImpactColor";
import { getMmrImr } from "#/utils/getMmrImr";

import { type TxModalVariant } from "../../../EquityModal.types";

interface SuccessSummaryProps {
  action: TxModalVariant;
  market: PortfolioSymbolType;
}

const generateData = ({
  equity,
  imr,
  mmr,
}: {
  equity: number;
  imr: number;
  mmr: number;
}) => [
  {
    data: {
      label: "Total equity",
      amount: equity,
      unit: "USD",
    },
    tooltip:
      "The total value of this account including open positions and available USDC.",
  },
  {
    data: {
      label: "IMr",
      amount: imr * 100,
      template: "0.00%",
    },
    tooltip: (
      <>
        <strong>IMr (Initial Margin Ratio) </strong>
        <br />A value greater than or equal to 100% means you have allocated all
        your Equity as margin, and hence have no available balance for
        increasing your position.
      </>
    ),
    leftLabelClassName: getImpactColor(imr),
  },
  {
    data: {
      label: "MMr",
      amount: mmr * 100,
      template: "0.00%",
    },
    tooltip: (
      <>
        <strong>MMr (Maintenance Margin Ratio)</strong>
        <br />A value greater than or equal to 100% means your account is under
        liquidation as there is not enough Equity to maintain open positions.
        Deposit collateral or open offsetting positions to reduce MMr.
      </>
    ),
    leftLabelClassName: getImpactColor(mmr),
  },
];

export const SuccessSummary: React.FC<SuccessSummaryProps> = ({ market }) => {
  const { account } = useAccount();
  const { accounts } = useAccounts();
  const { portfolioState } = useAccountState(market);

  const { imr, mmr } = getMmrImr({
    equity: portfolioState.equity,
    im: portfolioState.im,
    mm: portfolioState.mm,
  });
  const data = generateData({
    equity: portfolioState.equity,
    imr,
    mmr,
  });

  return (
    <>
      {Boolean(account) && (
        <div className="flex items-center">
          <InternalAccount
            address={account!}
            index={accounts.findIndex((safe) => safe === account)}
          />
          &nbsp;
          {`- ${market.toUpperCase()} Portfolio`}
        </div>
      )}
      <InfoGroup rows={data} variant="normal" showDivider={false} />
    </>
  );
};
